<template>
    <r-form
            v-if="form"
            ref="form"
            :column-size="1"
            :crud="true"
            :disabled.sync="disabled"
            :validators="validators"
            :showHeader="false"
            :columnOptions="{ md: 6 }"
            title="Compliance Types"
            @submit="save"
            v-model="form"
            hide-loading
            read-only-disabled-mode
            @update:loading="loading = $event"
            @select:changed="onSelectChanged"
    >
        <template slot="buttons"
                  v-if="!$can($perm.PROPERTIES_EDIT_PROPERTIES, $perm.PERMISSION_TYPE_PROPERTIES)"
        >
            <div></div>
        </template>
    </r-form>
</template>

<script>
import RForm from '@/components/Form/RiskhubForm';
import PropertyAssessmentType from '@/services/Property/PropertyAssessmentType';
import AssessmentType from '@/services/Assessment/AssessmentType';
import PropertyAssessmentTypeStatus from '@/services/Property/PropertyAssessmentTypeStatus';
import { cloneDeep, forEach, isObject, isEmpty, isUndefined } from 'lodash';
import { form as PropertyAssessmentTypeForm } from '@/services/Property/PropertyAssessmentTypeForm';
import { form as PropertyStatusForm, validators as PropertyStatusValidators } from '@/services/Property/PropertyStatusForm';
import Property from '@/services/Property/Property';
import ErrorMixin from '@/components/Notifications/ErrorMixin';
import { required, requiredIf } from 'vuelidate/lib/validators';

export default {
  name: 'Property-Assessment-Types',
  components: {
    RForm,
  },
  mixins: [ErrorMixin],
  props: {
    property: {
      required: true,
      type: Object,
    },
  },
  data() {
    return {
      allowedAssessmentTypes: [2, 14, 25],
      doorsAssessmentType: 14,
      disabled: true,
      hideCancel: true,
      loading: false,
      form: null,
      validators: PropertyStatusValidators,
      assessmentTypes: [],
      statusOptions: [],
      modals: {
        add: {
          visible: false,
        },
      },
    };
  },

  watch: {
    loading(newValue) {
      this.$emit('update:loading', newValue);
    },

    property: {
      handler(property) {
        this.form = null;
        this.disabled = true;
        this.getPropertyAssessmentTypes();
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    async getPropertyAssessmentTypes() {
      this.loading = true;

      try {
        const { data } = await PropertyAssessmentType.list(this.property.propertyId);
        this.assessmentTypes = data.data.filter(item => this.allowedAssessmentTypes.includes(item.assessmentTypeId));

        this.setForm();
      } catch (e) {
        this.$router.push({ name: 'properties' }, () => {
          this.$notify({
            title: 'Property compliance not found',
            text: 'Property compliance types could not be found.',
            type: 'error',
          });
        });
      } finally {
        this.loading = false;
      }
    },

    populateFields(form, values) {
      this.$set(form.propertyStatusId, 'value', this.property.propertyStatusId);
      this.$set(form.propertyStatusId, 'disabled', this.property.hasUnpublishedAssessments);
      this.$set(form.statusComment, 'value', this.property.statusComment);
      this.$set(form.statusComment, 'disabled', this.property.hasUnpublishedAssessments);

      if (this.property.designation) {
        this.$set(form.propertyDesignation, 'value', this.property.designation.propertyDesignation);
      }

      this.$set(form.clientPropertyDesignation, 'value', this.property.clientPropertyDesignation);

      forEach(values, (assessmentTypeData) => {
        const formKeyPrefix = 'assessmentTypes[' + assessmentTypeData.assessmentTypeId + ']';
        const enabledDisabled = !(assessmentTypeData.canDisable);
        const statusDisabled = !(assessmentTypeData.canDisable);
        this.$set(form[formKeyPrefix + '[enabled]'], 'disabled', enabledDisabled);
        this.$set(form[formKeyPrefix + '[propertyAssessmentTypeStatusId]'], 'disabled', statusDisabled);
        this.$set(form[formKeyPrefix + '[programme]'], 'value', { programme: this.property.programme });

        forEach(assessmentTypeData, (value, key) => {
          if (key === 'propertyAssessmentTypeStatusId') {
            value = this.statusOptions.find(row => row.propertyAssessmentTypeStatusId === value);
          }
          const formKey = `${formKeyPrefix}[${key}]`;
          if (formKey in form) {
            this.$set(form[formKey], 'value', value);
          }
        });
      });

      return form;
    },

    async setForm() {
      const formTemplate = PropertyAssessmentTypeForm();

      const params = { all: 1, parentTypeId: 0 };
      const assessmentTypesList = await AssessmentType.list(params);

      let assessmentTypes = assessmentTypesList.data.data;
      assessmentTypes = assessmentTypes.filter(item => this.allowedAssessmentTypes.includes(item.assessmentTypeId));

      const statusList = await PropertyAssessmentTypeStatus.list();
      this.statusOptions = statusList.data.data;

      let assessmentTypesForm = PropertyStatusForm();
      forEach(assessmentTypes, (item) => {
        const assessmentTypeKeyPrefix = `assessmentTypes[${item.assessmentTypeId}]`;
        forEach(formTemplate, (value, key) => {
          const formItem = _.cloneDeep(value);
          formItem.label = `${value.label} for ${item.assessmentType}`;
          const formItemKey = `${assessmentTypeKeyPrefix}[${key}]`;

          if (formItem.trackBy === 'propertyAssessmentTypeStatusId') {
            formItem.options = this.statusOptions;
          }

          assessmentTypesForm[formItemKey] = formItem;


          if (item.assessmentTypeId !== this.doorsAssessmentType) {
            if (key === 'programme') {
              formItem.hidden = true;
            } else if (key === 'propertyAssessmentTypeStatusId') {
              formItem.columnOptions.md = 4;
            }
          }


          if (key === 'statusComment') {
            const statusField = formItemKey.replace('statusComment', 'propertyAssessmentTypeStatusId');
            this.validators[formItemKey] = {
              requiredIf: requiredIf(() => {
                if (isUndefined(assessmentTypesForm[statusField].value)
                  || isEmpty(assessmentTypesForm[statusField].value)) {
                  return false;
                }
                return assessmentTypesForm[statusField].value.requiresComment;
              }),
            };
          }
        });
      });

      assessmentTypesForm = this.populateFields(assessmentTypesForm, this.assessmentTypes);
      this.form = assessmentTypesForm;
    },

    /**
     * Opens a modal by name (in data -> modals.[name])
     */
    openModal(name) {
      this.modals[name].visible = true;
      return true;
    },

    async save(form) {
      this.loading = true;

      const params = {
        assessmentTypes: {},
      };
      forEach(form, (object, field) => {
        const { value, disabled } = object;
        if (value === null) {
          return true;
        }

        if (disabled) {
          return true;
        }

        if (field.includes('assessmentTypes')) {
          const fieldNameParts = field.replace(/\]/g, '').split('[');

          if (fieldNameParts[2] === 'programme') {
            if (fieldNameParts[1] === this.doorsAssessmentType.toString()) {
              params.programme = value;
            }
          } else {
            params.assessmentTypes[fieldNameParts[1]] = params.assessmentTypes[fieldNameParts[1]] || {};
            params.assessmentTypes[fieldNameParts[1]][fieldNameParts[2]] = value;
          }
        } else if (field !== 'programme') {
          params[field] = object.value;
        }
      });

      try {
        const { data } = await Property.update(this.property.propertyId, params);

        this.$notify({
          title: 'Property compliance updated',
          message: 'Property compliance has been successfully updated',
          type: 'success',
        });

        this.disabled = true;
      } catch (e) {
        this.showValidationError(e);
      } finally {
        this.loading = false;
      }
    },

    onSelectChanged(data) {
      const { field, key } = data;
      if (field.value.propertyStatusId || field.value.programme) {
        return;
      }

      const statusKey = key.replace('propertyAssessmentTypeStatusId', 'statusComment');
      this.form[statusKey].value = '';
    },
  },
};
</script>
